'use client';

import type { ReactNode } from 'react';
import dynamic from 'next/dynamic';
import { useSearchParams } from 'next/navigation';
import { AnimatePresence, LazyMotion, domAnimation, m } from 'framer-motion';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useHomeOnboardingStatus } from '@/hooks/useHomeOnboardingStatus';
import { useUserLogged } from '@/hooks/useUserLogged';

// Toggle animation between admin and member home views. Subtle 6px slide +
// opacity keeps it "smooth af" without feeling theatrical. mode="wait" so
// the outgoing view finishes exit before the new one renders — avoids the
// two views fighting for layout space (their heights differ).
const viewVariants = {
  initial: { opacity: 0, y: 6 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -6 },
} as const;
/** Tiles in `QuickActionsRow` — keep in step so the placeholder grid matches. */
const QUICK_ACTION_TILES = 8;

const viewTransition = {
  duration: 0.22,
  ease: [0.4, 0, 0.2, 1] as const,
};

/**
 * Shared fallback — the same skeleton for session load, the latch fetch and the
 * dynamic import, so the user sees one continuous placeholder rather than a
 * flash between three of them.
 *
 * Shaped, not a slab. This used to be a single `h-[480px]` rectangle, which is
 * the one thing a skeleton must not be: it previews no structure, so the page
 * visibly rearranges itself the moment content lands. Both branches (admin and
 * member) open with the Ellie prompt row and the quick-actions grid, then a
 * section, so the placeholder mirrors that much — same grid classes, same tile
 * radius/border/padding as `QuickActionsRow`, same 6/10 gaps as the branches.
 */
export function HomeRouterFallback() {
  return (
    <div className="mb-8 flex flex-col gap-10" aria-hidden>
      <div className="flex flex-col gap-6">
        {/* Ellie prompt row — a label plus the rotating example pill. */}
        <div className="flex min-h-9 items-center gap-3">
          <Skeleton className="h-4 w-56 rounded" />
          <Skeleton className="h-8 w-80 rounded-full" />
        </div>

        {/* Quick actions — same track sizing as the real row, so the tiles do
            not resize when it swaps in. */}
        <div className="grid grid-flow-col auto-cols-fr gap-3">
          {Array.from({ length: QUICK_ACTION_TILES }).map((_, i) => (
            <div
              key={i}
              className="flex flex-col items-center gap-3 rounded-lg border border-ds-border-subtlest bg-ds-surface-white py-5"
            >
              <Skeleton className="size-10 rounded-xl" />
              <Skeleton className="h-3 w-16 rounded" />
            </div>
          ))}
        </div>
      </div>

      {/* First content section: heading line, then a narrow card beside a wide one. */}
      <div className="flex flex-col gap-4">
        <Skeleton className="h-3.5 w-40 rounded" />
        <div className="flex items-stretch gap-4">
          {/* Bordered shells with rows inside, not bare outlines: an empty
              320px box is the same "featureless rectangle" problem at a smaller
              size. */}
          <div className="flex w-[420px] shrink-0 flex-col overflow-hidden rounded-lg border border-ds-border-subtlest bg-ds-surface-white">
            <div className="flex items-center gap-3 border-b border-ds-border-subtlest px-5 py-4">
              <Skeleton className="size-11 rounded-full" />
              <div className="flex flex-1 flex-col gap-2">
                <Skeleton className="h-4 w-40 rounded" />
                <Skeleton className="h-3 w-24 rounded" />
              </div>
            </div>
            <div className="grid grid-cols-4 border-b border-ds-border-subtlest">
              {Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={i}
                  className={cn(
                    'flex flex-col gap-2 px-4 py-3',
                    i < 3 && 'border-r border-ds-border-subtlest'
                  )}
                >
                  <Skeleton className="h-[18px] w-10 rounded" />
                  <Skeleton className="h-3 w-12 rounded" />
                </div>
              ))}
            </div>
            <div className="flex flex-1 flex-col gap-2 px-5 py-4">
              <Skeleton className="h-3 w-24 rounded" />
              <Skeleton className="h-[102px] w-full rounded" />
            </div>
          </div>

          <div className="flex flex-1 flex-col overflow-hidden rounded-lg border border-ds-border-subtlest bg-ds-surface-white">
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className={cn(
                  'flex flex-col gap-2 px-5 py-4',
                  i > 0 && 'border-t border-ds-border-subtlest'
                )}
              >
                <Skeleton className="h-4 w-56 rounded" />
                <Skeleton className="h-3 w-3/4 rounded" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// Code-split each branch — user only downloads the one they hit.
const MemberHome = dynamic(() =>
  import('./member/CompletedHomeContent').then((m) => m.CompletedHomeContent)
);
const AdminHome = dynamic(() =>
  import('./admin/AdminCompletedHomeContent').then(
    (m) => m.AdminCompletedHomeContent
  )
);
const OnboardingBranch = dynamic(() =>
  import('./onboarding/OnboardingBranch').then((m) => m.OnboardingBranch)
);

// Inner component: orgUUID guaranteed present. useSuspenseQuery suspends
// up to the parent <Suspense>, so no intermediate loading state renders here.
function HomeRouterBranch({
  orgUUID,
  isAdmin,
  quickActions,
}: {
  orgUUID: string;
  isAdmin: boolean;
  quickActions: ReactNode;
}) {
  const { data } = useHomeOnboardingStatus(orgUUID);
  const isComplete = data?.value === 1;
  const searchParams = useSearchParams();
  // Admin opt-in to the member home via the MyViewToggle. Members never read
  // this param — they always render MemberHome.
  const isMyView = isAdmin && searchParams?.get('view') === 'member';

  if (!isComplete) {
    return <OnboardingBranch />;
  }

  const showAdmin = isAdmin && !isMyView;
  const viewKey = showAdmin ? 'admin' : 'member';

  return (
    <div className="mb-8">
      <LazyMotion features={domAnimation} strict>
        <AnimatePresence mode="wait" initial={false}>
          <m.div
            key={viewKey}
            variants={viewVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={viewTransition}
          >
            {showAdmin ? (
              <AdminHome quickActions={quickActions} />
            ) : (
              <MemberHome quickActions={quickActions} />
            )}
          </m.div>
        </AnimatePresence>
      </LazyMotion>
    </div>
  );
}

// Outer: waits for session. Renders the same skeleton while orgUUID is loading,
// then mounts the inner which suspends on the latch fetch up to the parent
// <Suspense> using the identical fallback — one continuous skeleton, no flash.
//
// `isAdmin` arrives from the server (`HomePageContent.tsx`) instead of being
// re-derived here from `useUserLogged()`. Two independent reads of "is this
// user admin" -- one baked into the frozen `quickActions` node, one live here
// -- could disagree across the 180s RSC payload cache: a soft nav could serve
// the stale quick-actions tile while this hook had already flipped, or vice
// versa. One source removes that.
export function HomeRouter({
  quickActions,
  isAdmin,
}: {
  quickActions: ReactNode;
  isAdmin: boolean;
}) {
  const { organization } = useUserLogged();
  const orgUUID = organization?.OrgUUID;
  if (!orgUUID) return <HomeRouterFallback />;
  return (
    <HomeRouterBranch
      orgUUID={orgUUID}
      isAdmin={isAdmin}
      quickActions={quickActions}
    />
  );
}
